import React, { useMemo } from 'react';
import { Radio } from 'antd';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { InPageSpinner } from '@components/spinners/in-page-spinner';
import { TooltipTop } from '@components/tooltips';
import { COLOR_STOPS, WEEKDAY_LABELS } from './constants';
import { dayPosition, heatColor } from './utils';
import { useCalendarTab } from './use-calendar-tab';
import styles from '../../styles.module.scss';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

type CalendarTabProps = {
    dagId: string;
    refreshKey: number;
};

/*
 * DAG detail's Calendar tab — a hand-built, GitHub-style heatmap of run activity by day/hour
 */
export function CalendarTab({ dagId, refreshKey }: CalendarTabProps) {
    const { year, month, daysInMonth, granularity, setGranularity, viewMode, setViewMode, monthTotal, monthFailed, successRate, cellMap, maxCount, isLoading, handleMonthChange } =
        useCalendarTab(dagId, refreshKey);

    const weekCount = useMemo(() => {
        const { week } = dayPosition(year, month, daysInMonth);
        return week + 1;
    }, [year, month, daysInMonth]);

    return (
        <div className={styles.card}>
            <div className={styles.filterRow}>
                <TooltipTop title="Previous month">
                    <span className={styles.actionIcon} onClick={() => handleMonthChange(-1)}>
                        <FiChevronLeft />
                    </span>
                </TooltipTop>
                <h3 style={{ margin: 0, minWidth: 160, color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: 0.25 }}>{`${MONTH_NAMES[month]} ${year}`}</h3>
                <TooltipTop title="Next month">
                    <span className={styles.actionIcon} onClick={() => handleMonthChange(1)}>
                        <FiChevronRight />
                    </span>
                </TooltipTop>
                <div className={styles.filterRowEnd}>
                    <Radio.Group value={granularity} onChange={(e) => setGranularity(e.target.value)} buttonStyle="solid" size="small">
                        <Radio.Button value="daily">Daily</Radio.Button>
                        <Radio.Button value="hourly">Hourly</Radio.Button>
                    </Radio.Group>
                    <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)} buttonStyle="solid" size="small">
                        <Radio.Button value="total">Total</Radio.Button>
                        <Radio.Button value="failed">Failed</Radio.Button>
                    </Radio.Group>
                </div>
            </div>

            <div className={styles.metaGrid} style={{ marginBottom: 20 }}>
                <div className={styles.metaField}>
                    <div className={styles.metaLabel}>Total Runs</div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: '#e1e1e6' }}>{monthTotal}</div>
                </div>
                <div className={styles.metaField}>
                    <div className={styles.metaLabel}>Failed</div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: '#ff4d4f' }}>{monthFailed}</div>
                </div>
                <div className={styles.metaField}>
                    <div className={styles.metaLabel}>Success Rate</div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: '#08c46a' }}>{`${successRate}%`}</div>
                </div>
            </div>

            {isLoading ? (
                <InPageSpinner size="Small" />
            ) : granularity === 'daily' ? (
                <DailyHeatmap year={year} month={month} daysInMonth={daysInMonth} weekCount={weekCount} cellMap={cellMap} maxCount={maxCount} viewMode={viewMode} />
            ) : (
                <HourlyHeatmap daysInMonth={daysInMonth} cellMap={cellMap} maxCount={maxCount} viewMode={viewMode} />
            )}

            <HeatLegend viewMode={viewMode} />
        </div>
    );
}

type HeatmapProps = {
    cellMap: Map<string, { total: number; failed: number }>;
    maxCount: number;
    viewMode: 'total' | 'failed';
};

/*
 * 7-row (Mon–Sun) by N-week-column daily heatmap grid
 */
function DailyHeatmap({ year, month, daysInMonth, weekCount, cellMap, maxCount, viewMode }: HeatmapProps & { year: number; month: number; daysInMonth: number; weekCount: number }) {
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    return (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, justifyContent: 'space-between' }}>
                {WEEKDAY_LABELS.map((label) => (
                    <div key={label} style={{ height: 28, fontSize: 11, color: '#666a8d', display: 'flex', alignItems: 'center' }}>
                        {label}
                    </div>
                ))}
            </div>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${weekCount}, 28px)`,
                    gridTemplateRows: 'repeat(7, 28px)',
                    gap: 4,
                }}
            >
                {days.map((day) => {
                    const { weekday, week } = dayPosition(year, month, day);
                    const cell = cellMap.get(`${day}-0`);
                    const count = viewMode === 'failed' ? cell?.failed ?? 0 : cell?.total ?? 0;
                    const color = heatColor(count, maxCount, viewMode);
                    return (
                        <TooltipTop key={day} title={`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}: ${cell?.total ?? 0} total, ${cell?.failed ?? 0} failed`}>
                            <div
                                style={{
                                    gridColumn: week + 1,
                                    gridRow: weekday + 1,
                                    width: 28,
                                    height: 28,
                                    borderRadius: 4,
                                    background: color ?? '#2e2f45',
                                    border: '1px solid #3e4161',
                                }}
                            />
                        </TooltipTop>
                    );
                })}
            </div>
        </div>
    );
}

/*
 * 24-hour-row by day-of-month-column hourly heatmap, horizontally scrollable
 */
function HourlyHeatmap({ daysInMonth, cellMap, maxCount, viewMode }: HeatmapProps & { daysInMonth: number }) {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    return (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, overflowX: 'auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {hours.map((hour) => (
                    <div key={hour} style={{ height: 20, fontSize: 10, color: '#666a8d', display: 'flex', alignItems: 'center' }}>
                        {String(hour).padStart(2, '0')}
                    </div>
                ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, 20px)`, gridTemplateRows: `repeat(24, 20px)`, gap: 2 }}>
                {days.map((day) =>
                    hours.map((hour) => {
                        const cell = cellMap.get(`${day}-${hour}`);
                        const count = viewMode === 'failed' ? cell?.failed ?? 0 : cell?.total ?? 0;
                        const color = heatColor(count, maxCount, viewMode);
                        return (
                            <TooltipTop key={`${day}-${hour}`} title={`Day ${day}, ${String(hour).padStart(2, '0')}:00 — ${cell?.total ?? 0} total, ${cell?.failed ?? 0} failed`}>
                                <div
                                    style={{
                                        gridColumn: day,
                                        gridRow: hour + 1,
                                        width: 20,
                                        height: 20,
                                        borderRadius: 3,
                                        background: color ?? '#2e2f45',
                                        border: '1px solid #3e4161',
                                    }}
                                />
                            </TooltipTop>
                        );
                    })
                )}
            </div>
        </div>
    );
}

/*
 * 5-step color-ramp legend matching the active view mode
 */
function HeatLegend({ viewMode }: { viewMode: 'total' | 'failed' }) {
    const stops = COLOR_STOPS[viewMode];
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666a8d' }}>
            <span>Less</span>
            {stops.map((color) => (
                <span key={color} style={{ width: 14, height: 14, borderRadius: 3, background: color, border: '1px solid #3e4161' }} />
            ))}
            <span>More</span>
        </div>
    );
}
